import { useCallback, useEffect, useState } from "react";
import { confirm } from "./ConfirmDialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { MapPin, Plus, Archive, Crown, Edit2 } from "lucide-react";
import { apiClient } from "../App";
import { UpgradeButton } from "./SubscriptionUI";

const EMPTY_FORM = {
  name: "",
  timezone: "America/New_York",
  street: "",
  city: "",
  state: "",
  zip: "",
};

const TIMEZONES = [
  { v: "America/New_York", l: "Eastern Time (ET)" },
  { v: "America/Chicago", l: "Central Time (CT)" },
  { v: "America/Denver", l: "Mountain Time (MT)" },
  { v: "America/Los_Angeles", l: "Pacific Time (PT)" },
  { v: "America/Phoenix", l: "Arizona Time" },
];

/**
 * Locations management panel for Settings → Company.
 *
 * Behavior:
 *  - Every tenant shows their current locations (at least 1 after backfill).
 *  - Free/Starter tenants see a "Multi-location is a Pro feature" upgrade card.
 *  - Pro tenants can add / rename / archive locations. Archive is blocked
 *    server-side when active employees remain (surfaced via toast).
 */
export const LocationsPanel = ({ tenant }) => {
  const plan = tenant?.plan || "free";
  const canAddMore = plan === "pro";

  const [locations, setLocations] = useState([]);
  const [defaultLocationId, setDefaultLocationId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchLocations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/locations");
      setLocations(res.data?.locations || []);
      setDefaultLocationId(res.data?.default_location_id || null);
    } catch {
      toast.error("Failed to load locations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, timezone: tenant?.timezone || "America/New_York" });
    setDialogOpen(true);
  };
  const openEdit = (loc) => {
    setEditing(loc);
    setForm({
      name: loc.name || "",
      timezone: loc.timezone || "America/New_York",
      street: loc.address?.street || "",
      city: loc.address?.city || "",
      state: loc.address?.state || "",
      zip: loc.address?.zip || "",
    });
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Location name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        timezone: form.timezone,
        address: {
          street: form.street.trim() || null,
          city: form.city.trim() || null,
          state: form.state.trim() || null,
          zip: form.zip.trim() || null,
        },
      };
      if (editing) {
        await apiClient.patch(`/locations/${editing.location_id}`, payload);
        toast.success("Location updated");
      } else {
        await apiClient.post("/locations", payload);
        toast.success("Location added");
      }
      setDialogOpen(false);
      await fetchLocations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const archive = async (loc) => {
    const ok = await confirm({
      title: `Archive "${loc.name}"?`,
      description: "The location will be hidden from selectors going forward, but all historical time entries, payroll, and reports remain intact. You can restore it later from the archived list.",
      confirmText: "Archive location",
      variant: "warning",
    });
    if (!ok) return;
    try {
      await apiClient.delete(`/locations/${loc.location_id}`);
      toast.success("Location archived");
      await fetchLocations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Archive failed");
    }
  };

  // Show upgrade card for non-Pro tenants once they already have 1 location backfilled.
  const showUpgradeCard = !canAddMore && locations.length >= 1;

  return (
    <Card className="border border-slate-200" data-testid="locations-panel">
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#0D9488]" /> Locations
            </CardTitle>
            <CardDescription>
              Manage physical locations. Pro plan is billed{" "}
              <span className="font-medium text-[#0A192F]">$99/month per location</span>.
            </CardDescription>
          </div>
          {canAddMore && (
            <Button size="sm" onClick={openCreate} data-testid="add-location-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Location
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading && <p className="text-sm text-slate-500">Loading…</p>}

        {!loading && locations.length === 0 && (
          <p className="text-sm text-slate-500">No locations found.</p>
        )}

        {locations.map((loc) => (
          <div
            key={loc.location_id}
            className="flex items-start justify-between gap-3 border rounded-md p-3 bg-white"
            data-testid={`location-row-${loc.location_id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-medium text-[#0A192F]" data-testid="location-name">
                  {loc.name}
                </p>
                {loc.location_id === defaultLocationId && (
                  <Badge variant="secondary" className="text-[10px]">
                    Default
                  </Badge>
                )}
                <span className="text-xs text-slate-500">· {loc.timezone}</span>
              </div>
              {loc.address && (loc.address.street || loc.address.city) && (
                <p className="text-xs text-slate-500 mt-0.5">
                  {[loc.address.street, loc.address.city, loc.address.state, loc.address.zip]
                    .filter(Boolean)
                    .join(", ")}
                </p>
              )}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => openEdit(loc)}
                data-testid={`edit-location-${loc.location_id}`}
                title="Edit"
              >
                <Edit2 className="w-4 h-4" />
              </Button>
              {locations.length > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => archive(loc)}
                  data-testid={`archive-location-${loc.location_id}`}
                  title="Archive"
                >
                  <Archive className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        ))}

        {showUpgradeCard && (
          <div className="mt-3 border border-dashed border-slate-300 bg-slate-50/40 rounded-md p-4 flex items-start gap-3">
            <Crown className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="font-medium text-[#0A192F]">Multi-location is a Pro feature</p>
              <p className="text-xs text-slate-500">
                Upgrade to Pro to add more locations. Each location bills at $99/mo — pay only
                for what you use.
              </p>
            </div>
            <UpgradeButton size="sm" plan="pro" />
          </div>
        )}

        {canAddMore && locations.length >= 1 && (
          <p className="text-xs text-slate-500 pt-1" data-testid="locations-billing-preview">
            Preview: <span className="font-medium">${locations.length * 99}/mo</span> ={" "}
            {locations.length} location{locations.length > 1 ? "s" : ""} × $99.
            {tenant?.plan_is_grandfathered && (
              <span className="ml-1 text-amber-600 font-medium">
                Grandfathered — you&apos;ll continue at $99 flat 🎉
              </span>
            )}
          </p>
        )}
      </CardContent>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="location-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Location" : "Add Location"}</DialogTitle>
            <DialogDescription>
              {editing
                ? "Update location details."
                : "New locations bill at $99/mo (prorated on Pro plan)."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div>
              <Label>Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Downtown Store"
                required
                data-testid="location-name-input"
              />
            </div>
            <div>
              <Label>Timezone</Label>
              <Select
                value={form.timezone}
                onValueChange={(v) => setForm({ ...form, timezone: v })}
              >
                <SelectTrigger data-testid="location-timezone-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map((tz) => (
                    <SelectItem key={tz.v} value={tz.v}>
                      {tz.l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Street</Label>
              <Input
                value={form.street}
                onChange={(e) => setForm({ ...form, street: e.target.value })}
                data-testid="location-street-input"
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>City</Label>
                <Input
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  data-testid="location-city-input"
                />
              </div>
              <div>
                <Label>State</Label>
                <Input
                  value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value })}
                  data-testid="location-state-input"
                />
              </div>
              <div>
                <Label>ZIP</Label>
                <Input
                  value={form.zip}
                  onChange={(e) => setForm({ ...form, zip: e.target.value })}
                  data-testid="location-zip-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                data-testid="location-cancel-btn"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="location-save-btn">
                {saving ? "Saving…" : editing ? "Save changes" : "Add location"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
